/**
 * Horizon's <theme-drawer> "squeezes" page content sideways instead of
 * overlaying it on wide viewports (≥990px) — a hardcoded behavior in the
 * core theme-drawer.js/theme-drawer.liquid, no exposed setting to opt out.
 * Rather than editing those Horizon files, this hooks the same public
 * `theme-drawer:open`/`theme-drawer:close` events (see theme-drawer.js
 * DrawerOpenEvent/DrawerCloseEvent) to layer our own dark overlay behind the
 * cart drawer specifically, and force off the squeeze margin it would
 * otherwise apply — only below the same 990px breakpoint Horizon itself
 * uses, since under that the drawer already opens as a true native modal
 * (showModal()) with its own backdrop, and we'd just be doubling it up.
 */
const CART_DRAWER_ID = 'cart-drawer';
const squeezeQuery = window.matchMedia('(min-width: 990px)');

/** @type {HTMLDivElement | undefined} */
let overlay;

function getOverlay() {
  if (overlay) return overlay;

  overlay = document.createElement('div');
  overlay.className = 'akr-drawer-overlay';
  overlay.addEventListener('click', () => {
    /** @type {any} */ (document.getElementById(CART_DRAWER_ID))?.close?.();
  });
  document.body.append(overlay);

  return overlay;
}

/** @param {{ instant?: boolean }} [options] */
function applyOpenState({ instant = false } = {}) {
  const pageWrapper = document.querySelector('.page-wrapper');
  if (pageWrapper instanceof HTMLElement) {
    // Wins over the class-driven margin regardless of the squeeze
    // coordination script in theme-drawer.liquid adding/removing that class.
    pageWrapper.style.marginRight = '0';
  }

  const el = getOverlay();
  if (instant) {
    // Matches the drawer panel's own instant-restore treatment
    // (.theme-drawer__dialog-instant) — appearing pre-open on load
    // shouldn't also fade in.
    el.style.transition = 'none';
    el.classList.add('is-visible');
    requestAnimationFrame(() => requestAnimationFrame(() => el.style.removeProperty('transition')));
  } else {
    el.classList.add('is-visible');
  }
}

function applyClosedState() {
  const pageWrapper = document.querySelector('.page-wrapper');
  if (pageWrapper instanceof HTMLElement) {
    pageWrapper.style.marginRight = '';
  }

  overlay?.classList.remove('is-visible');
}

document.addEventListener('theme-drawer:open', (event) => {
  if (event.target instanceof Element === false || event.target.id !== CART_DRAWER_ID) return;
  if (!squeezeQuery.matches) return;

  applyOpenState();
});

document.addEventListener('theme-drawer:close', (event) => {
  if (event.target instanceof Element === false || event.target.id !== CART_DRAWER_ID) return;

  applyClosedState();
});

// theme-drawer.liquid's synchronous restore script (session persistence,
// squeeze mode only) runs before this module ever gets a chance to attach
// listeners, and dispatches 'theme-drawer:restoring' (not 'theme-drawer:open')
// on the drawer element without `bubbles: true` — unobservable from here
// either way. Check the state it already left behind instead.
const restoredDrawer = document.getElementById(CART_DRAWER_ID);
if (restoredDrawer?.hasAttribute('open') && squeezeQuery.matches) {
  applyOpenState({ instant: true });
}

// Empty-cart "Continue shopping" link (cart-products.liquid, core) is a
// plain <a> with no close behavior — left as-is, it navigates away while
// the drawer is still flagged open, so theme-drawer.liquid's session
// restore reopens it on the very next page. `close()` is async (waits out
// the closing animation before it dispatches theme-drawer:close, which is
// what updates sessionStorage) — navigating immediately would race it, so
// we wait for it to settle before following the link ourselves.
document.addEventListener('click', (event) => {
  const link =
    event.target instanceof Element ? event.target.closest('a.cart-items__empty-button') : null;
  if (!link || !(link instanceof HTMLAnchorElement) || !link.closest(`#${CART_DRAWER_ID}`)) return;

  event.preventDefault();
  const drawer = /** @type {any} */ (document.getElementById(CART_DRAWER_ID));
  Promise.resolve(drawer?.close?.()).finally(() => {
    window.location.href = link.href;
  });
});
